import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Compass } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCurrency } from "@/lib/CurrencyContext";

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { currency, setCurrency } = useCurrency();

  const links = [
    { to: "/", label: "Home" },
    { to: "/tours", label: "Tours" },
    { to: "/blog", label: "Blog" },
    { to: "/my-bookings", label: "My Bookings" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="glass-nav fixed top-0 left-0 right-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
          <Compass className="w-5 h-5 text-sandy" />
          <span className="font-cormorant text-xl text-midnight tracking-tight">
            Keyz's <span className="font-light">Epic Adventure Tours</span>
          </span>
        </Link>

        {/* Desktop Links */}
        <div className="hidden sm:flex items-center gap-6">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`navbar-${link.label.toLowerCase().replace(/\s/g, '-')}-link`}
              className={`font-manrope text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${
                isActive(link.to) ? "text-sandy" : "text-midnight/60 hover:text-midnight"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger data-testid="currency-switcher" className="w-[80px] h-7 text-xs font-manrope border-midnight/10 bg-transparent">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">$ USD</SelectItem>
              <SelectItem value="EUR">&euro; EUR</SelectItem>
              <SelectItem value="XCD">EC$ XCD</SelectItem>
            </SelectContent>
          </Select>
          <Link
            to="/admin"
            data-testid="navbar-admin-link"
            className="font-manrope text-xs uppercase tracking-[0.2em] text-midnight/30 hover:text-sandy transition-colors duration-200"
          >
            Admin
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="sm:hidden text-midnight"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="sm:hidden bg-white/95 backdrop-blur-xl border-t border-midnight/5 px-6 py-6 space-y-4" data-testid="mobile-menu">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className={`block font-manrope text-sm uppercase tracking-[0.15em] ${
                isActive(link.to) ? "text-sandy" : "text-midnight/60"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/admin"
            onClick={() => setMobileOpen(false)}
            className="block font-manrope text-sm uppercase tracking-[0.15em] text-midnight/30"
          >
            Admin
          </Link>
        </div>
      )}
    </nav>
  );
};
